import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { ExploreTabs } from '../components/ExploreTabs';
import { ExploreHero } from '../components/ExploreHero';
import { SiteFooter } from '../components/SiteFooter';
import { SaleCard, humanise, type PublicSale } from '../components/SaleCard';
import { useApi } from '../lib/useApi';
import { count } from '../lib/format';
import { Button, EmptyState, ErrorBlock, LoadingBlock, Select, TextInput } from '../components/ui';
import '../styles/public.css';
import '../styles/discover.css';

/**
 * Buy Property — the public search for properties for sale.
 *
 * Filters live in the URL rather than in component state, for the reason the
 * rental search gives: a property search is a thing people send to somebody
 * else, and state that cannot be linked to cannot be sent.
 *
 * What differs from renting is the money and the paperwork. The price range is
 * in millions, so the inputs take millions; and tenure is a filter of its own,
 * because a buyer who will not touch a leasehold is not going to read forty
 * listings to find that out one at a time.
 */

interface Facets {
  counties: { county: string; n: number }[];
  types: { property_type: string; n: number }[];
}
interface Response {
  listings: PublicSale[];
  total: number; available: number; limit: number; offset: number;
  facets: Facets;
  amenities: { code: string; label: string }[];
  propertyTypes: { code: string; label: string }[];
}

const BEDROOM_CHOICES = [1, 2, 3, 4, 5];

/** Prices are typed in millions and sent in shillings. */
const toShillings = (millions: string) => {
  const n = Number(millions);
  return Number.isFinite(n) && millions.trim() !== '' ? String(Math.round(n * 1_000_000)) : '';
};
const toMillions = (shillings: string) => {
  const n = Number(shillings);
  return Number.isFinite(n) && shillings ? String(n / 1_000_000) : '';
};

export function BuyProperty() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');

  const get = (k: string) => params.get(k) ?? '';
  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    next.delete('offset');
    setParams(next);
  };

  const offset = Number(get('offset') || 0);
  const list = useApi<Response>('/api/v1/sales', {
    q: get('q') || undefined,
    county: get('county') || undefined,
    propertyType: get('propertyType') || undefined,
    bedrooms: get('bedrooms') || undefined,
    minPrice: get('minPrice') || undefined,
    maxPrice: get('maxPrice') || undefined,
    tenure: get('tenure') || undefined,
    amenities: get('amenities') || undefined,
    offset: offset || undefined,
  });

  // Its own request, not a slice of the list: the strip is what a showcase
  // place buys, and it must not move when a buyer pages through the results.
  const showcase = useApi<{ listings: PublicSale[] }>('/api/v1/sales/showcase', { limit: 3 });

  const data = list.data;
  const chosenAmenities = get('amenities').split(',').filter(Boolean);
  const toggleAmenity = (code: string) => {
    const next = chosenAmenities.includes(code)
      ? chosenAmenities.filter((a) => a !== code)
      : [...chosenAmenities, code];
    set({ amenities: next.join(',') });
  };

  const shown = data?.listings.length ?? 0;
  const filtered = ['q', 'county', 'propertyType', 'bedrooms', 'minPrice', 'maxPrice', 'tenure', 'amenities']
    .some((k) => get(k));

  return (
    <>
      <PublicHeader />

      <div className="dsc-hero-inner" style={{ paddingTop: 16, paddingBottom: 0 }}>
        <ExploreTabs path="/buy" />
      </div>

      <div className="dsc-hero-inner" style={{ paddingTop: 0 }}>
        <ExploreHero
          section="buy"
          title="Buy property"
          sub="Houses, apartments and land for sale across Kenya, listed by the people selling them."
        >
          <form className="dsc-search" onSubmit={(e) => { e.preventDefault(); set({ q }); }}>
            <TextInput aria-label="Search by area or description" value={q}
              placeholder="Try an area — Runda, Nanyuki, Syokimau…"
              onChange={(e) => setQ(e.target.value)} />
            <Button type="submit">Search</Button>
          </form>
        </ExploreHero>
      </div>

      {/*
        * The showcase strip — the thing a showcase place is sold to appear in.
        *
        * Hidden the moment somebody filters: a buyer who has said "Kajiado,
        * under 5M" is answering their own question, and a paid strip of
        * something else on top of their results is an advertisement in the way
        * of what they asked for. It is not labelled "paid" as a row, because
        * every card in it carries its own Featured or Premium badge.
        */}
      {!filtered && (showcase.data?.listings.length ?? 0) > 0 && (
        <section className="dsc-showcase" aria-label="Showcase">
          <h2 className="dsc-showcase-title">Showcase</h2>
          <div className="dsc-grid">
            {showcase.data!.listings.map((l) => <SaleCard key={l.id} listing={l} />)}
          </div>
        </section>
      )}

      <div className="dsc-body">
        <aside className="dsc-filters" aria-label="Filters">
          <div className="dsc-filter">
            <label className="dsc-filter-label" htmlFor="b-county">County</label>
            {/* Offered from what is actually on the site, so a filter can never
                propose an area with nothing in it. */}
            <Select id="b-county" value={get('county')} onChange={(e) => set({ county: e.target.value })}>
              <option value="">Anywhere</option>
              {(data?.facets.counties ?? []).map((c) => (
                <option key={c.county} value={c.county}>{c.county} ({c.n})</option>
              ))}
            </Select>
          </div>

          <div className="dsc-filter">
            <label className="dsc-filter-label" htmlFor="b-type">Property type</label>
            <Select id="b-type" value={get('propertyType')}
              onChange={(e) => set({ propertyType: e.target.value })}>
              <option value="">Any type</option>
              {(data?.facets.types ?? []).map((t) => (
                <option key={t.property_type} value={t.property_type}>
                  {humanise(t.property_type)} ({t.n})
                </option>
              ))}
            </Select>
          </div>

          <div className="dsc-filter">
            <label className="dsc-filter-label" htmlFor="b-tenure">Tenure</label>
            <Select id="b-tenure" value={get('tenure')} onChange={(e) => set({ tenure: e.target.value })}>
              <option value="">Freehold or leasehold</option>
              <option value="freehold">Freehold</option>
              <option value="leasehold">Leasehold</option>
              <option value="sectional">Sectional title</option>
            </Select>
          </div>

          <div className="dsc-filter">
            <span className="dsc-filter-label">Bedrooms</span>
            <div className="dsc-chips">
              {BEDROOM_CHOICES.map((n) => (
                <button key={n} type="button"
                  className={`dsc-chip${get('bedrooms') === String(n) ? ' on' : ''}`}
                  aria-pressed={get('bedrooms') === String(n)}
                  onClick={() => set({ bedrooms: get('bedrooms') === String(n) ? '' : String(n) })}>
                  {n}+
                </button>
              ))}
            </div>
          </div>

          <div className="dsc-filter">
            {/* Millions, because typing 45000000 into a box is how a search for
                a 45 million shilling house becomes a search for 4.5 million. */}
            <span className="dsc-filter-label">Price (KES millions)</span>
            <div className="dsc-range">
              <TextInput type="number" inputMode="decimal" aria-label="Minimum price in millions"
                placeholder="Min" defaultValue={toMillions(get('minPrice'))}
                onBlur={(e) => set({ minPrice: toShillings(e.target.value) })} />
              <span className="dsc-range-dash" aria-hidden>–</span>
              <TextInput type="number" inputMode="decimal" aria-label="Maximum price in millions"
                placeholder="Max" defaultValue={toMillions(get('maxPrice'))}
                onBlur={(e) => set({ maxPrice: toShillings(e.target.value) })} />
            </div>
          </div>

          <div className="dsc-filter">
            <span className="dsc-filter-label">Features</span>
            <div className="dsc-chips">
              {(data?.amenities ?? []).map((a) => (
                <button key={a.code} type="button"
                  className={`dsc-chip${chosenAmenities.includes(a.code) ? ' on' : ''}`}
                  aria-pressed={chosenAmenities.includes(a.code)}
                  onClick={() => toggleAmenity(a.code)}>
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {filtered && (
            <Button variant="ghost" size="sm"
              onClick={() => { setQ(''); setParams(new URLSearchParams()); }}>
              Clear all filters
            </Button>
          )}
        </aside>

        <main className="dsc-results">
          {list.loading ? <LoadingBlock />
            : list.error ? <ErrorBlock error={list.error} onRetry={list.reload} />
              : shown === 0 ? (
                <EmptyState
                  title={filtered ? 'Nothing matches that search' : 'No properties listed for sale yet'}
                  message={filtered
                    ? 'Try a wider price range, a different area, or fewer features.'
                    : 'Properties appear here as sellers publish them. Check back soon.'} />
              ) : (
                <>
                  <p className="dsc-count">
                    {/* Counts what can actually be enquired on. Sold and
                        reserved adverts stay below, but calling them "for sale"
                        is what keeping them visible was meant to avoid. */}
                    {count(data!.available)} propert{data!.available === 1 ? 'y' : 'ies'} for sale
                    {data!.total > data!.available && (
                      <span className="muted">
                        {' '}· {count(data!.total - data!.available)} sold or under offer
                      </span>
                    )}
                  </p>
                  <div className="dsc-grid">
                    {data!.listings.map((l) => <SaleCard key={l.id} listing={l} />)}
                  </div>

                  {data!.total > shown + offset && (
                    <div className="dsc-more">
                      <Button variant="secondary"
                        onClick={() => set({ offset: String(offset + data!.limit) })}>
                        Show more
                      </Button>
                    </div>
                  )}
                  {offset > 0 && (
                    <div className="dsc-more">
                      <Button variant="ghost" size="sm"
                        onClick={() => set({ offset: String(Math.max(0, offset - data!.limit)) })}>
                        Back a page
                      </Button>
                    </div>
                  )}
                </>
              )}
        </main>
      </div>

      <section className="dsc-cta">
        <h2>Selling a property?</h2>
        <p>Advertise it here and reach buyers searching in your area.</p>
        <Link className="btn btn-primary" to="/sell">Sell your property</Link>
      </section>

      <SiteFooter />
    </>
  );
}
